import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useToast } from "../../hooks/useToast";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import TicketDetailModal from "../../components/admin/TicketDetailModal";
import OpenTicketModal from "../../components/admin/OpenTicketModal";
import InitialsAvatar from "../../components/common/InitialsAvatar";
import AdminFiltersModal, {
  FiltersButton,
  FilterChips,
  buildFilterChips,
  countActiveFilters,
  removeFilterValue,
} from "../../components/admin/AdminFiltersModal";
import { resolveDateRange } from "../../utils/dateRangePresets";
import { useAuth } from "../../hooks/useAuth";
import { canViewAgencies } from "../../utils/agencyAccess";
import api from "../../services/api";
import ListLoadError from "../../components/admin/ListLoadError";
import TasksPage, { TASK_SECTIONS } from "./TasksPage";
import styles from "./SupportTicketsPage.module.css";

// Canonical backend enums (api/config/constants.js) with friendly labels
const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "waiting", label: "Waiting" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];
const CLAIM_OPTIONS = [
  { value: "claimed", label: "Claimed" },
  { value: "unclaimed", label: "Unclaimed" },
];
const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];
const CATEGORY_OPTIONS = [
  { value: "order", label: "Order" },
  { value: "product", label: "Product" },
  { value: "account", label: "Account" },
  { value: "technical", label: "Technical" },
  { value: "billing", label: "Billing" },
  { value: "other", label: "Other" },
];

// The Support workspace. Queue/Archive share the ticket list; the two Tasks
// tabs mount TasksPage's own sections, so no capability moved house with the
// nav item and /admin/tasks still renders standalone.
const TABS = [
  { key: "queue", label: "Queue" },
  { key: "my-work", label: "My Work" },
  { key: "needs-attention", label: "Needs Attention" },
  { key: "archive", label: "Archive" },
];
const DEFAULT_TAB = "queue";

// The two ticket lists and the status set each one means. Queue is the ACTIVE
// work; Archive is its TERMINAL counterpart. The sets partition every status
// the backend defines, so no ticket is reachable from neither tab.
// `waiting` belongs in the Queue even though it has no badge style yet
// (TICKETS-AUDIT.md § UI/UX); the badge gap is a separate concern.
// Archive deliberately does NOT send `archived=true`: that param selects a
// separate VIEW (supportTicketRepository:135-139 keys on archived_at IS NULL
// xor IS NOT NULL, never both), and asking for it hid every resolved and
// closed ticket behind a list nothing in web/ can populate.
// TODO(6233): #6233 shipped an archive/restore API, so in principle this tab
// should show archived ∪ terminal. It is a no-op today and is deliberately NOT
// implemented: `archived=true` returns 0 rows, and no page in web/ calls the
// archive/restore endpoints, so archived_at cannot become non-empty from the
// product. Because the repository view is exclusive, a union needs TWO requests
// merged client-side — and it would break #6626 c1, whose oracle
// (GET ?status=resolved) is itself a non-archived read. Add the union in the
// same card that adds the archive affordance, and re-derive that oracle then.
const TAB_STATUSES = {
  queue: ["open", "in_progress", "waiting"],
  archive: ["resolved", "closed"],
};
// First load shows only the newest slice; `Expand All` loads the whole queue.
const DEFAULT_LIMIT = 10;
// The list endpoint caps `limit` at 100 (MAX_LIST_LIMIT in the controller), so
// a queue longer than that is assembled from consecutive pages.
const MAX_PAGE_SIZE = 100;

const isTicketListTab = (tab) => Boolean(TAB_STATUSES[tab]);

const readTabFromParams = (searchParams) => {
  const tab = searchParams.get("tab");
  return TABS.some(({ key }) => key === tab) ? tab : DEFAULT_TAB;
};

// URL query params owned by the Filters experience (assignedTo doubles as
// the Tasks team-view deep link — the deep link IS the Claimed By filter)
const SELECT_FILTER_PARAMS = ["status", "claim", "assignedTo", "priority", "category", "tier", "agency"];

// Card 53585: "A, B and C" rather than "A and B and C" when more than one
// filter's option source failed.
const describeFilterList = (labels) =>
  labels.length <= 1
    ? labels.join("")
    : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;

const readFiltersFromParams = (searchParams) => {
  const values = {};
  SELECT_FILTER_PARAMS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) values[key] = value;
  });
  const preset = searchParams.get("preset");
  if (preset) {
    values.created = {
      preset: preset.toUpperCase(),
      start: searchParams.get("from") || "",
      end: searchParams.get("to") || "",
    };
  }
  return values;
};

const writeFiltersToParams = (values, prevParams) => {
  const params = new URLSearchParams();
  // preserve non-filter params (e.g. ?ticket= search seed, and the active tab —
  // applying a filter must not bounce you back to Queue)
  const ticket = prevParams.get("ticket");
  if (ticket) params.set("ticket", ticket);
  const tab = prevParams.get("tab");
  if (tab) params.set("tab", tab);
  SELECT_FILTER_PARAMS.forEach((key) => {
    if (values[key]) params.set(key, values[key]);
  });
  const created = values.created;
  if (created?.preset) {
    // an incomplete custom range applies nothing — don't persist it
    const isComplete = created.preset !== "CUSTOM" || (created.start && created.end);
    if (isComplete) {
      params.set("preset", created.preset);
      if (created.preset === "CUSTOM") {
        params.set("from", created.start);
        params.set("to", created.end);
      }
    }
  }
  return params;
};

const SupportTicketsPage = () => {
  const { toast } = useToast();
  const { user, hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Whole-queue view is a transient view choice, deliberately NOT in the URL:
  // a reload returns to the 10-row default rather than re-fetching every row.
  const [expanded, setExpanded] = useState(false);
  // Seed search from ?ticket=<number> (Tasks dashboard "Open" quick action)
  const [search, setSearch] = useState(() => searchParams.get("ticket") || "");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [claimants, setClaimants] = useState([]);
  const [customTiers, setCustomTiers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  // Card 53585: labels of the option sources whose load genuinely FAILED
  // (a permission 403 is not a failure -- see loadFilterOptions below).
  const [optionErrors, setOptionErrors] = useState([]);

  // Shared ticket detail modal (the modal fetches its own detail + history)
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  // Client-first create-ticket modal (the same component the member profile renders)
  const [openTicketModalOpen, setOpenTicketModalOpen] = useState(false);

  // URL is the single source of truth for filter state AND the active tab
  const filters = useMemo(() => readFiltersFromParams(searchParams), [searchParams]);
  const activeTab = useMemo(() => readTabFromParams(searchParams), [searchParams]);
  // The Tasks "Open" quick action arrives as ?ticket=<number>. It used to come
  // from another route, so mounting was enough to seed the box; now Tasks is a
  // tab on THIS page it is a same-route change and must be read every time.
  const ticketSeed = searchParams.get("ticket") || "";

  useEffect(() => {
    if (ticketSeed) setSearch(ticketSeed);
  }, [ticketSeed]);

  useEffect(() => {
    // The Tasks tabs render their own data — don't pay for a ticket page there
    if (isTicketListTab(activeTab)) loadTickets(1, ticketSeed || search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Card 53585: a select with ZERO options is skipped outright by
  // AdminFiltersModal (its :264 predicate keeps a filter only when
  // `(def.options?.length ?? 0) > 0`), so a failed options load does not render
  // an empty dropdown — the whole labelled control DISAPPEARS. An operator then
  // reads "could not load" as "this filter does not exist here", and there is no
  // empty-state string anywhere to contradict them.
  //
  // A PREDICTED 403 is NOT a load failure: these endpoints are gated by other
  // permission modules, so a denial keeps the original fail-soft silence
  // (card 49621). Only a genuine failure earns a voice. `error.status` is set on
  // every api.js error (services/api.js:255), which is what separates the arms.
  const loadFilterOptions = useCallback(() => {
    setOptionErrors([]);
    const noteFailure = (label, error) => {
      if (error?.status === 403) return;
      setOptionErrors((prev) => (prev.includes(label) ? prev : [...prev, label]));
    };
    api.get("/admin/support-tickets/claimants")
      .then((response) => setClaimants(response.claimants || []))
      .catch((error) => {
        setClaimants([]);
        noteFailure("Claimed by", error);
      });
    api.get("/admin/custom-membership-tiers")
      .then((response) => setCustomTiers((response.tiers || []).filter((t) => t.isActive)))
      .catch((error) => {
        setCustomTiers([]);
        noteFailure("Custom tier", error);
      });
    // Card 49621: this one is not merely "gated by another module" — without
    // agencies.view it can only 403, so do not spend the request at all.
    if (canViewAgencies(hasPermission)) {
      api.get("/admin/agencies")
        .then((response) => setAgencies(response.agencies || []))
        .catch((error) => {
          setAgencies([]);
          noteFailure("Entity / Agency", error);
        });
    } else {
      setAgencies([]);
    }
  }, [hasPermission]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Query string for one page of the active tab's list. An explicit Status
  // filter narrows within the tab, so it replaces the tab's base status set.
  const buildListParams = (page, limit, searchTerm) => {
    const params = new URLSearchParams({ page, limit });
    if (searchTerm) params.append("search", searchTerm);
    if (filters.status) params.append("status", filters.status);
    else if (TAB_STATUSES[activeTab]) {
      params.append("statuses", TAB_STATUSES[activeTab].join(","));
    }
    if (filters.claim) params.append("claimState", filters.claim);
    if (filters.assignedTo) params.append("assignedTo", filters.assignedTo);
    if (filters.priority) params.append("priority", filters.priority);
    if (filters.category) params.append("category", filters.category);
    if (filters.tier) params.append("customTierId", filters.tier);
    if (filters.agency) params.append("agencyId", filters.agency);
    const range = resolveDateRange(filters.created);
    if (range) {
      params.append("dateFrom", range.from);
      params.append("dateTo", range.to);
    }
    return params;
  };

  // The complete list in one view: page 1 tells us the total, the remaining
  // pages are fetched together. Reported as a single page so the pager hides.
  const fetchEveryPage = async (searchTerm) => {
    const first = await api.get(`/admin/support-tickets?${buildListParams(1, MAX_PAGE_SIZE, searchTerm)}`);
    const total = first.pagination?.total ?? 0;
    const remaining = Math.max(Math.ceil(total / MAX_PAGE_SIZE) - 1, 0);
    const rest = await Promise.all(
      Array.from({ length: remaining }, (_, index) =>
        api.get(`/admin/support-tickets?${buildListParams(index + 2, MAX_PAGE_SIZE, searchTerm)}`)
      )
    );
    return {
      tickets: [first, ...rest].flatMap((response) => response.tickets || []),
      pagination: { ...first.pagination, page: 1, limit: total, totalPages: 1 },
    };
  };

  const loadTickets = async (page = 1, searchTerm = search, expandAll = false) => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = expandAll
        ? await fetchEveryPage(searchTerm)
        : await api.get(`/admin/support-tickets?${buildListParams(page, DEFAULT_LIMIT, searchTerm)}`);
      setTickets(response.tickets || []);
      setPagination(response.pagination);
      setExpanded(expandAll);
    } catch (error) {
      console.error("Failed to load tickets:", error);
      toast.error("Failed to load support tickets");
      setLoadError(true);
      setTickets([]);
      setExpanded(false);
    } finally {
      setLoading(false);
    }
  };

  const claimantOptions = useMemo(() => {
    const options = claimants.map((admin) => ({
      value: admin.id,
      label: admin.fullName || admin.email,
    }));
    // A deep-linked ?assignedTo= uuid may not be among current assignees —
    // resolve its label from loaded rows (AR page precedent) so the chip
    // and select stay meaningful.
    const current = filters.assignedTo;
    if (current && !options.some((opt) => opt.value === current)) {
      const fromRows = tickets.find((t) => t.assignedTo?.id === current)?.assignedTo;
      options.push({
        value: current,
        label: fromRows?.fullName || `Admin ${current.slice(0, 8)}…`,
      });
    }
    return options;
  }, [claimants, filters.assignedTo, tickets]);

  const filterDefs = useMemo(
    () => [
      { key: "created", label: "Created", type: "dateRange" },
      { key: "status", label: "Status", type: "select", placeholder: "All statuses", options: STATUS_OPTIONS },
      { key: "claim", label: "Claim state", type: "select", placeholder: "Any", options: CLAIM_OPTIONS },
      { key: "assignedTo", label: "Claimed by", type: "select", placeholder: "Anyone", options: claimantOptions },
      { key: "priority", label: "Priority", type: "select", placeholder: "All priorities", options: PRIORITY_OPTIONS },
      { key: "category", label: "Category", type: "select", placeholder: "All categories", options: CATEGORY_OPTIONS },
      {
        key: "tier",
        label: "Custom tier",
        type: "select",
        placeholder: "All custom tiers",
        options: customTiers.map((tier) => ({ value: tier.id, label: tier.name })),
      },
      {
        key: "agency",
        label: "Entity / Agency",
        type: "select",
        placeholder: "All agencies",
        options: agencies.map((agency) => ({ value: agency.id, label: agency.name })),
      },
    ],
    [claimantOptions, customTiers, agencies]
  );

  const activeFilterCount = countActiveFilters(filters);
  const filterChips = useMemo(() => buildFilterChips(filterDefs, filters), [filterDefs, filters]);

  const applyFilters = (nextValues) => {
    setSearchParams(writeFiltersToParams(nextValues, searchParams));
    setFiltersOpen(false);
  };

  const handleRemoveChip = (chip) => {
    applyFilters(removeFilterValue(filters, chip));
  };

  const handleResetFilters = () => {
    applyFilters({});
  };

  // Tab lives in the same searchParams as the filters, so it survives a reload
  // and a filter change without a second source of truth.
  const handleSelectTab = (key) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", key);
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadTickets(1, search);
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicketId(ticket.id);
    setViewModalOpen(true);
  };

  const handleClaimTicket = async (ticket) => {
    try {
      await api.post(`/admin/support-tickets/${ticket.id}/claim`);
      toast.success("Ticket claimed");
      loadTickets(pagination?.page || 1, search, expanded);
    } catch (error) {
      if (error.status === 403) {
        // First-claim-wins: another agent already owns it. Name the owner
        // from the ticket's own assignedTo (the 403 body drops it), falling
        // back to a fresh detail fetch for the current owner after a race.
        let owner = ticket.assignedTo?.fullName;
        try {
          const detail = await api.get(`/admin/support-tickets/${ticket.id}`);
          owner = detail.ticket?.assignedTo?.fullName || owner;
        } catch (e) {
          // keep the list-derived owner name
        }
        toast.error(
          owner
            ? `Already claimed by ${owner}`
            : "This ticket is already claimed by another agent"
        );
        loadTickets(pagination?.page || 1, search);
      } else {
        toast.error("Failed to claim ticket");
      }
    }
  };

  const isClaimedByMe = (ticket) => {
    const myId = user?.id ?? user?._id;
    return Boolean(
      ticket?.assignedTo?.id && myId && ticket.assignedTo.id === myId
    );
  };

  const getStatusClass = (status) => {
    const statusMap = {
      open: styles.statusOpen,
      in_progress: styles.statusInProgress,
      resolved: styles.statusResolved,
      closed: styles.statusClosed,
    };
    return statusMap[status] || styles.statusOpen;
  };

  const getPriorityClass = (priority) => {
    const priorityMap = {
      low: styles.priorityLow,
      normal: styles.priorityNormal,
      medium: styles.priorityNormal,
      high: styles.priorityHigh,
      urgent: styles.priorityUrgent,
    };
    return priorityMap[priority] || styles.priorityNormal;
  };

  const formatStatus = (status) =>
    status.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Support Tickets</h1>
          <p>Manage customer support requests</p>
        </div>
      </div>

      <div className={styles.tabs} role="tablist" aria-label="Support workspace">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeTab === key}
            className={`${styles.tab} ${activeTab === key ? styles.tabActive : ""}`}
            onClick={() => handleSelectTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === "my-work" && (
        <TasksPage sections={[TASK_SECTIONS.MY_WORK]} />
      )}
      {activeTab === "needs-attention" && (
        <TasksPage sections={[TASK_SECTIONS.ATTENTION]} />
      )}

      {isTicketListTab(activeTab) && (
        <>
        {/* Toolbar: free-text search + consolidated Filters */}
        <div className={styles.toolbar}>
          <form onSubmit={handleSearch} className={styles.searchForm}>
            <Input
              placeholder="Search by subject, user, or ticket #..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button type="submit">Search</Button>
          </form>

          <FiltersButton
            activeCount={activeFilterCount}
            onClick={() => setFiltersOpen(true)}
          />

          {optionErrors.length > 0 && (
            <div className={styles.optionsError} role="alert">
              <span>
                {`${describeFilterList(optionErrors)} ${
                  optionErrors.length > 1 ? "filters" : "filter"
                } could not be loaded`}
              </span>
              <button
                type="button"
                className={styles.optionsRetryBtn}
                onClick={loadFilterOptions}
              >
                Retry
              </button>
            </div>
          )}

          {hasPermission("support_tickets.edit") && (
            <button
              type="button"
              className={styles.openTicketBtn}
              data-testid="open-ticket-button"
              aria-haspopup="dialog"
              onClick={() => setOpenTicketModalOpen(true)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Open Ticket
            </button>
          )}

          <FilterChips chips={filterChips} onRemove={handleRemoveChip} />
        </div>

        {/* Tickets Table */}
        <div className={styles.tableCard}>
          {loading ? (
            <div className={styles.loading}>
              <LoadingSpinner />
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Subject</th>
                  <th>User</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td className={styles.ticketNumber}>#{ticket.ticketNumber}</td>
                    <td>
                      <div className={styles.subjectCell}>
                        <span className={styles.subject}>{ticket.subject}</span>
                      </div>
                    </td>
                    <td>
                      <Link
                        to={`/admin/members/${ticket.user?.id}`}
                        className={`${styles.userCell} ${styles.userLink}`}
                      >
                        <span className={styles.userName}>
                          {ticket.user?.firstName} {ticket.user?.lastName}
                        </span>
                        <span className={styles.userEmail}>
                          {ticket.user?.email}
                        </span>
                      </Link>
                    </td>
                    <td>{ticket.category}</td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${getStatusClass(ticket.status)}`}
                      >
                        {formatStatus(ticket.status)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${styles.priorityBadge} ${getPriorityClass(ticket.priority)}`}
                      >
                        {ticket.priority?.charAt(0).toUpperCase() +
                          ticket.priority?.slice(1)}
                      </span>
                    </td>
                    <td className={styles.dateCell}>
                      {new Date(ticket.createdAt).toLocaleDateString("en-US", {
                        month: "numeric",
                        day: "numeric",
                        year: "numeric",
                      })}
                      ,{" "}
                      {new Date(ticket.createdAt).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        {ticket.assignedTo ? (
                          <div
                            className={styles.claimedCue}
                            title={`Claimed by ${ticket.assignedTo.fullName}${
                              isClaimedByMe(ticket) ? " (you)" : ""
                            }`}
                          >
                            <InitialsAvatar
                              firstName={ticket.assignedTo.firstName}
                              lastName={ticket.assignedTo.lastName}
                              size="sm"
                            />
                            <span className={styles.claimedName}>
                              {isClaimedByMe(ticket)
                                ? "You"
                                : ticket.assignedTo.firstName ||
                                  ticket.assignedTo.fullName}
                            </span>
                          </div>
                        ) : (
                          <button
                            className={styles.claimBtn}
                            onClick={() => handleClaimTicket(ticket)}
                            title="Claim this ticket"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <line x1="19" y1="8" x2="19" y2="14"></line>
                              <line x1="22" y1="11" x2="16" y2="11"></line>
                            </svg>
                            <span>Claim</span>
                          </button>
                        )}
                        <button
                          className={styles.viewBtn}
                          onClick={() => handleViewTicket(ticket)}
                          title="View ticket"
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!loading && loadError && (
            <ListLoadError
              message="Could not load support tickets"
              onRetry={() => loadTickets(1, search)}
            />
          )}

          {!loading && !loadError && tickets.length === 0 && (
            <div className={styles.empty}>
              {activeTab === "archive"
                ? "No resolved or closed tickets"
                : "No support tickets found"}
            </div>
          )}
        </div>

        {/* Count is always shown; Expand All swaps the pager for the full list.
            An empty list says so in the table itself — no "Showing 0 of 0". */}
        {pagination && tickets.length > 0 && (
          <div className={styles.pagination}>
            <span>
              Showing {tickets.length} of {pagination.total}
            </span>
            <div className={styles.paginationBtns}>
              {!expanded && pagination.total > tickets.length && (
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => loadTickets(1, search, true)}
                >
                  Expand All
                </Button>
              )}
              {!expanded && pagination.totalPages > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="small"
                    disabled={pagination.page <= 1}
                    onClick={() => loadTickets(pagination.page - 1, search)}
                  >
                    Previous
                  </Button>
                  <span className={styles.pageInfo}>
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="small"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => loadTickets(pagination.page + 1, search)}
                  >
                    Next
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
        </>
      )}

      <AdminFiltersModal
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filterDefs}
        values={filters}
        onApply={applyFilters}
        onReset={handleResetFilters}
      />

      {/* Create-ticket modal. No memberId here, so it opens with the required
          Client picker; the member profile passes one and it does not. */}
      <OpenTicketModal
        isOpen={openTicketModalOpen}
        onClose={() => setOpenTicketModalOpen(false)}
        onCreated={() => loadTickets(1, search, expanded)}
      />

      {/* Shared ticket detail modal (same component on the client profile) */}
      <TicketDetailModal
        ticketId={selectedTicketId}
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        onUpdated={() => loadTickets(pagination?.page || 1, search, expanded)}
      />
    </div>
  );
};

export default SupportTicketsPage;
